/**
 * useToast Hook
 *
 * Provides toast notification functionality using Sonner.
 * Wraps Sonner's toast API for consistent usage across the app.
 */

import { toast as sonnerToast, type ExternalToast } from 'sonner';

import { UI_CONFIG } from '@/config/app.config';

type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading';

interface ToastOptions extends ExternalToast {
  /** Duration in milliseconds */
  duration?: number;
}

/**
 * useToast - Show toast notifications
 *
 * @example
 * const toast = useToast();
 *
 * // Success toast
 * toast.success('User created successfully');
 *
 * // Error toast
 * toast.error('Failed to create user');
 *
 * // With options
 * toast.success('Saved!', { duration: 3000 });
 */
export function useToast() {
  const defaultOptions: ToastOptions = {
    duration: UI_CONFIG.toastDuration,
  };

  /**
   * Show a success toast
   */
  const success = (message: string, options?: ToastOptions) => {
    return sonnerToast.success(message, { ...defaultOptions, ...options });
  };

  /**
   * Show an error toast
   */
  const error = (message: string, options?: ToastOptions) => {
    return sonnerToast.error(message, { ...defaultOptions, ...options });
  };

  /**
   * Show a warning toast
   */
  const warning = (message: string, options?: ToastOptions) => {
    return sonnerToast.warning(message, { ...defaultOptions, ...options });
  };

  /**
   * Show an info toast
   */
  const info = (message: string, options?: ToastOptions) => {
    return sonnerToast.info(message, { ...defaultOptions, ...options });
  };

  /**
   * Show a loading toast (returns dismiss function)
   */
  const loading = (message: string, options?: ToastOptions) => {
    return sonnerToast.loading(message, { ...defaultOptions, ...options });
  };

  /**
   * Show a promise-based toast
   * Shows loading, then success or error based on promise result
   */
  const promise = <T,>(
    promiseArg: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: unknown) => string);
    }
  ) => {
    return sonnerToast.promise(promiseArg, messages);
  };

  /**
   * Dismiss a specific toast or all toasts
   */
  const dismiss = (toastId?: string | number) => {
    return sonnerToast.dismiss(toastId);
  };

  /**
   * Show a custom toast with explicit type
   */
  const show = (type: ToastType, message: string, options?: ToastOptions) => {
    switch (type) {
      case 'success':
        return success(message, options);
      case 'error':
        return error(message, options);
      case 'warning':
        return warning(message, options);
      case 'info':
        return info(message, options);
      case 'loading':
        return loading(message, options);
      default:
        return sonnerToast(message, { ...defaultOptions, ...options });
    }
  };

  return {
    success,
    error,
    warning,
    info,
    loading,
    promise,
    dismiss,
    show,
  };
}

// ============================================
// Standalone toast functions (for use outside React components)
// ============================================

export const toast = {
  success: (message: string, options?: ToastOptions) =>
    sonnerToast.success(message, { duration: UI_CONFIG.toastDuration, ...options }),

  error: (message: string, options?: ToastOptions) =>
    sonnerToast.error(message, { duration: UI_CONFIG.toastDuration, ...options }),

  warning: (message: string, options?: ToastOptions) =>
    sonnerToast.warning(message, { duration: UI_CONFIG.toastDuration, ...options }),

  info: (message: string, options?: ToastOptions) =>
    sonnerToast.info(message, { duration: UI_CONFIG.toastDuration, ...options }),

  loading: (message: string, options?: ToastOptions) =>
    sonnerToast.loading(message, { duration: UI_CONFIG.toastDuration, ...options }),

  dismiss: (toastId?: string | number) => sonnerToast.dismiss(toastId),
};
